import React, {
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";

const CompareRevisionsButton = forwardRef(function CompareRevisionsButton(
  { modelingData, setModelingData, revKey },
  ref
) {
  const [open, setOpen] = useState(false);

  const revisions = useMemo(() => {
    if (!modelingData || typeof modelingData !== "object") return [];

    return Object.keys(modelingData)
      .filter((key) => !key.startsWith("__"))
      .sort((a, b) => Number(a) - Number(b));
  }, [modelingData]);

  const normalizeValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value).trim();
  };

  useImperativeHandle(ref, () => ({
    triggerOpen: () => {
      setOpen(true);
    },
    triggerClose: () => {
      setOpen(false);
    },
  }));

  const compareTables = (currentRows, revisionRows) => {
    const current = Array.isArray(currentRows) ? currentRows : [];
    const old = Array.isArray(revisionRows) ? revisionRows : [];

    const oldMap = new Map(old.map((row) => [String(row.uid), row]));
    const currentMap = new Map(current.map((row) => [String(row.uid), row]));

    const allRowIds = new Set([
      ...Array.from(oldMap.keys()),
      ...Array.from(currentMap.keys()),
    ]);

    const result = [];

    allRowIds.forEach((id) => {
      const currentRow = currentMap.get(id) || null;
      const oldRow = oldMap.get(id) || null;

      if (currentRow && !oldRow) {
        result.push({
          ...currentRow,
          __status: "added",
          __diffs: {},
          __oldRow: null,
        });
        return;
      }

      if (!currentRow && oldRow) {
        result.push({
          ...oldRow,
          __status: "removed",
          __diffs: {},
          __oldRow: oldRow,
        });
        return;
      }

      const allKeys = Array.from(
        new Set([
          ...Object.keys(currentRow || {}),
          ...Object.keys(oldRow || {}),
        ])
      );

      const __diffs = {};
      let changed = false;

      allKeys.forEach((key) => {
        if (key.startsWith("__")) return;

        const currentValue = normalizeValue(currentRow?.[key]);
        const oldValue = normalizeValue(oldRow?.[key]);

        if (currentValue !== oldValue) {
          __diffs[key] = {
            oldValue: oldRow?.[key],
            newValue: currentRow?.[key],
          };
          changed = true;
        }
      });

      result.push({
        ...currentRow,
        __status: changed ? "changed" : "same",
        __diffs,
        __oldRow: oldRow,
      });
    });

    return result;
  };

  const handleSelectRevision = (selectedRev) => {
    const currentRows = Array.isArray(modelingData?.[revKey])
      ? modelingData[revKey]
      : [];

    const revisionRows = Array.isArray(modelingData?.[selectedRev])
      ? modelingData[selectedRev]
      : [];

    const comparedRows = compareTables(currentRows, revisionRows);

    setModelingData((prev) => ({
      ...prev,
      [revKey]: comparedRows,
      __compareMode: true,
      __compareRevision: selectedRev,
      __compareTargetRevKey: revKey,
    }));

    setOpen(false);
  };

  const handleClearCompare = () => {
    setModelingData((prev) => {
      const cleaned = { ...prev };

      if (Array.isArray(cleaned?.[revKey])) {
        cleaned[revKey] = cleaned[revKey].map((row) => {
          const newRow = { ...row };
          delete newRow.__status;
          delete newRow.__diffs;
          delete newRow.__oldRow;
          return newRow;
        });
      }

      delete cleaned.__compareMode;
      delete cleaned.__compareRevision;
      delete cleaned.__compareTargetRevKey;

      return cleaned;
    });

    setOpen(false);
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        onClick={() => setOpen(true)}
      >
        Compare Revisions
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Available Revisions</DialogTitle>

        <DialogContent>
          <List>
            {revisions.map((rev) => (
              <ListItemButton
                key={rev}
                onClick={() => handleSelectRevision(rev)}
              >
                <ListItemText primary={`Revision ${rev}`} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>

        <DialogActions>
          <Button color="error" onClick={handleClearCompare}>
            Clear Compare
          </Button>

          <Button onClick={() => setOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default CompareRevisionsButton;